import { PREFERRED_CALENDAR_VIEW } from 'constants/user';
import { DateTime } from 'luxon';
import type {
  CombinedParameters,
  Filter,
  FilterSpan,
  RouteParameters,
  UrlParameters,
} from 'redux/modules/calendar.types';
import { Ref, Trigger } from 'redux/modules/calendar.types';
import { getCurrentAccount } from 'redux/selectors/accounts';
import { getHourDraftUserId } from 'redux/selectors/hours.draft.base';
import { getNonDeletedUserIds } from 'redux/selectors/project.editor';
import { getRoutePathName, getRouting } from 'redux/selectors/routing';
import { getHasEnabledTic, getUserId } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { getDaysInSpan, isBetweenDates } from 'utils/date-helper';
import { encodeUrlParams } from 'utils/url-params';
import type { ParserDef } from 'utils/url-parser';
import {
  boolParser,
  dateStringParser,
  enumParser,
  numberArrayParser,
  numberParser,
  parseWithDef,
  timeParser,
} from 'utils/url-parser';

const readDefaultCalendarView = (state: IState) =>
  (state.user.properties[PREFERRED_CALENDAR_VIEW] as FilterSpan) ?? 'day';

const calendarUrlParametersParser: ParserDef<UrlParameters> = {
  date: dateStringParser,
  userIds: numberArrayParser,
  multiUserMode: boolParser,
  total_minutes: numberParser,
  start_time: timeParser,
  trigger: (input: string | Array<string>) => enumParser(Trigger, input),
  timesheet: boolParser,
  ref: (input: string | Array<string>) => enumParser(Ref, input),
};

/**
 * This reads span from path via redux state slice from redux-first-history
 * This allows us to avoid a bunch of ParametricSelector's
 */
const readSpan = createSelector([getRoutePathName], (pathname): FilterSpan | undefined => {
  if (pathname.includes('day')) {
    return 'day';
  }
  if (pathname.includes('week')) {
    return 'week';
  }
  if (pathname.includes('month')) {
    return 'month';
  }
  return undefined;
});

export const getRouteParameters = createSelector(
  [readSpan, readDefaultCalendarView],
  (span, defaultCalendarView): RouteParameters => {
    return {
      span: span && ['day', 'week', 'month'].includes(span) ? span : defaultCalendarView,
    };
  }
);

export const getUrlParameters = createSelector(
  [getRouting, getHasEnabledTic],
  (routing, hasEnabledTic): UrlParameters => {
    const parseResult = parseWithDef<UrlParameters>(
      routing.location?.search ?? '',
      calendarUrlParametersParser
    );

    // parse errors will fail silently logging warnings to console and fallback to defaults
    if (parseResult.hasError) {
      Object.values(parseResult.errors).forEach(value => {
        console.warn(value);
      });
    }

    const parsedFilterFromUrl = parseResult.result;

    return {
      date: parsedFilterFromUrl.date || DateTime.now().toISODate(),
      multiUserMode: parsedFilterFromUrl.multiUserMode || false,
      userIds: parsedFilterFromUrl.userIds || [],
      trigger: parsedFilterFromUrl.trigger,
      total_minutes: parsedFilterFromUrl.total_minutes,
      start_time: parsedFilterFromUrl.start_time,
      tic: hasEnabledTic ? true : undefined,
      timesheet: parsedFilterFromUrl.timesheet,
      ref: parsedFilterFromUrl.ref,
    };
  }
);

export const getCombinedUrlParameters = createSelector(
  [getRouteParameters, getUrlParameters],
  (routeParameters, urlParameters): CombinedParameters => {
    return {
      ...routeParameters,
      ...urlParameters,
    };
  }
);

const readStoredFilter = (state: IState) => state.calendar.filter;

type AsQueryParameters<T extends object> = {
  [_K in keyof Required<T>]: undefined | string | boolean | number | Array<number> | Array<string>;
};
export const getDefaultCalendarRoute = createSelector(
  [readStoredFilter, getUrlParameters, readDefaultCalendarView, getHasEnabledTic],
  (storedFilter, currentUrlParameters, defaultCalendarView, hasEnabledTic): string => {
    const routeParameters: RouteParameters = {
      span: storedFilter.span ?? defaultCalendarView,
    };
    const urlParameters: AsQueryParameters<UrlParameters> = {
      multiUserMode: storedFilter.multiUserMode || false,
      date: currentUrlParameters.date || storedFilter.date || DateTime.now().toISODate(),
      userIds: storedFilter.userIds || [],
      total_minutes: currentUrlParameters.total_minutes,
      trigger: currentUrlParameters.trigger,
      start_time: currentUrlParameters.start_time?.raw,
      tic: hasEnabledTic ? true : undefined,
      timesheet: currentUrlParameters.timesheet,
      ref: currentUrlParameters.ref,
    };
    return `/calendar/${routeParameters.span}${encodeUrlParams(urlParameters)}`;
  }
);

export const getCalendarUserId = createSelector(
  [getCombinedUrlParameters, getUserId, getHourDraftUserId, getNonDeletedUserIds],
  (combinedUrlParams, userId, forUserId, nonDeletedUserIds): number => {
    // Returns the user id we should act as for hour API calls, in order:
    // - Owner of the hour draft being edited
    // - First user in filter.userIds (unless in multiuser mode)
    // - The authenticated user
    const calendarUserId =
      forUserId != null
        ? forUserId
        : combinedUrlParams.userIds.length && !combinedUrlParams.multiUserMode
          ? // If users haven't loaded yet, optimistically act as if the selected user is active so we
            // can avoid loading unused data (like AI score) and initialize on-mount data correctly
            !nonDeletedUserIds.length || nonDeletedUserIds.includes(combinedUrlParams.userIds[0])
            ? combinedUrlParams.userIds[0]
            : userId
          : userId;

    return calendarUserId;
  }
);

export const getFilter = createSelector(
  [getCombinedUrlParameters, getUserId, getCalendarUserId, readDefaultCalendarView],
  (combinedUrlParams, userId, calendarUserId, defaultCalendarView): Filter => {
    const isActingAsSelf = calendarUserId === userId;

    // Force switch span if not supported by current user(s)
    // Note that this is done on selector level, not changing the actual url
    // This allows us to navigate back to 'day' when switching back to 'solo'
    const span =
      !isActingAsSelf && combinedUrlParams.span === 'day'
        ? 'week'
        : combinedUrlParams.multiUserMode && ['day', 'month'].includes(combinedUrlParams.span)
          ? 'week'
          : combinedUrlParams.span;

    // Don't add trigger, total_minutes, and timesheet to filter
    const { trigger, total_minutes, timesheet, ...restCombinedParams } = combinedUrlParams;

    const calculatedFilter: Filter = {
      ...restCombinedParams,
      calendarUserId,
      isActingAsSelf,
      span: span ?? defaultCalendarView,
    };
    return calculatedFilter;
  }
);

export const getDates = createSelector(
  [getFilter, getCurrentAccount],
  (filter, account): Array<string> => {
    return getDaysInSpan(filter.date, filter.span, account.start_of_week, true);
  }
);

export const getDerivedDatesInfo = createSelector([getDates], dates => {
  const since = dates[0];
  const upto = dates[dates.length - 1];
  const isTodayInView = isBetweenDates(DateTime.now().toISODate(), since, upto);
  const isSingleDay = dates.length === 1;

  return {
    since,
    upto,
    isTodayInView,
    isSingleDay,
  };
});
